/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createAsyncApiCall } from 'src/core/services/api/createAsyncApiCall';
import { httpService } from 'src/core/services/api/httpService';
import { AsyncApiCallHandler } from 'src/core/types/AsyncApiCallHandler';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { AnalyticsStatus } from 'src/luis/projects/analytics/types/AnalyticsStatus';
import { AnalyticsStatusSwagger } from 'src/luis/projects/analytics/types/swagger/AnalyticsStatusSwagger';
import { AnalyticsSwagger } from 'src/luis/projects/analytics/types/swagger/AnalyticsSwagger';
import { EndpointHitsSwagger } from 'src/luis/projects/analytics/types/swagger/EndpointHitsSwagger';
import { IntentFaultyUtterancesSwagger } from 'src/luis/projects/analytics/types/swagger/IntentFaultyUtterancesSwagger';
import { LuisId } from 'src/luis/types/Resource';

/**
 * Represents a service for manipulation of analytics.
 *
 * @param appId The app id to get the analytics for.
 */
export const getAnalyticsService = createService(({ context, getEnvironment }) => {
    const { webApiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url scheme for this service.
     *
     * @param versionId The id of the version to use.
     * @returns A url string to call.
     * A helper function to build the url.
     */
    const getUrlBuilder = (usedVersionId?: string) =>
        urlBuilder()
            .baseUrl(webApiUrl)
            .app(context.appId)
            .version(usedVersionId || context.versionId);

    /**
     * Gets the analytics results fpr the given app version.
     *
     * @param usedVersionId The id of the version to use.
     * @returns A promise with the analytics results.
     */
    const getMetadata = async (usedVersionId?: string): Promise<AnalyticsSwagger> => {
        const requestUrl = getUrlBuilder(usedVersionId)
            .resource('statsmetadata')
            .build();

        const { data: analyticsApiItem } = await httpService.get<AnalyticsSwagger>(requestUrl, apiOptions);

        return analyticsApiItem;
    };

    /**
     * Gets the url where the status of the calculation process of the statistics in
     * the backend side.
     *
     * @param usedVersionId The id of the version to use.
     * @returns URL to be hit.
     */
    const getStatusOperationUrl = async (usedVersionId?: string): Promise<string> => {
        const requestUrl = getUrlBuilder(usedVersionId)
            .resource('stats')
            .build();

        const { headers: statusOperationUrlHeader } = await httpService.post(requestUrl, {}, apiOptions);
        let url = (<Record<string, string>>statusOperationUrlHeader)['operation-location'];

        if (isProduction) {
            url = url.replace('/api/', '/webapi/');
        }

        return url;
    };

    /**
     * Gets the status of the calculations of the analytics in the backend side
     * either it is completed or failed and in case of ny other status it keeps
     * trying to get the status until this status becomes either completed or failed.
     *
     * @param url The operation URL to get the status from.
     * @param handler The Handler to handle the behavior of the asynchronous call.
     * @returns either the status is completed or not.
     */
    const getStatus = async (url: string, handler: AsyncApiCallHandler<AnalyticsStatus, AnalyticsStatusSwagger>): Promise<boolean> =>
        (await createAsyncApiCall<AnalyticsStatus, AnalyticsStatusSwagger>(url, apiOptions, handler)).status === 2;

    /**
     * Gets the last training date for the given version
     * if it was ever trained and null if it was not trained before.
     *
     * @param usedVersionId The version to get the training date of.
     * @returns Analytics store contains only the training date if exists.
     */
    const getLastTrainingDate = async (usedVersionId?: string): Promise<string> => {
        const requestUrl = getUrlBuilder(usedVersionId)
            .resource('state')
            .build();

        const { data: lastTrainingDate } = await httpService.get<string>(requestUrl, apiOptions);

        return lastTrainingDate;
    };

    /**
     * Gets the training data for the given intent.
     *
     * @param intentId The id of the intent to be fetched.
     * @returns Intent training data.
     */
    const getIntentTrainingResult = async (intentId: LuisId): Promise<IntentFaultyUtterancesSwagger> => {
        const requestUrl = getUrlBuilder()
            .resource('intents', intentId)
            .resource('stats')
            .build();

        const { data: faultyIntent } = await httpService.get<IntentFaultyUtterancesSwagger>(requestUrl, apiOptions);

        return faultyIntent;
    };

    /**
     * Gets the endpoint hits history with respect to the given start date and the end date.
     *
     * @param startDate The starting date.
     * @param endDate The ending date.
     */
    const getAppEndpointHitsHistory = async (startDate: Date, endDate: Date): Promise<EndpointHitsSwagger> => {
        const requestUrl = getUrlBuilder()
            .resource('stats')
            .resource('endpointHitsHistory')
            .queryParam('from', startDate.toISOString())
            .queryParam('to', endDate.toISOString())
            .build();

        const { data: endpointMap } = await httpService.get<EndpointHitsSwagger>(requestUrl, apiOptions);

        return endpointMap;
    };

    return {
        getStatus,
        getMetadata,
        getLastTrainingDate,
        getStatusOperationUrl,
        getIntentTrainingResult,
        getAppEndpointHitsHistory
    };
});
